import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';
import { GUIDED_TOURS } from './manualContent';

const GuidedTour = ({ tourId, onEnd }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const tooltipRef = useRef(null);

  const tour = GUIDED_TOURS.find(t => t.id === tourId);
  const steps = tour?.steps || [];
  const step = steps[currentStep];

  const findAndHighlight = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top - 6,
        left: rect.left - 6,
        width: rect.width + 12,
        height: rect.height + 12,
      });
      el.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    findAndHighlight();
    const interval = setInterval(findAndHighlight, 800);
    return () => clearInterval(interval);
  }, [findAndHighlight]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onEnd?.();
      if (e.key === 'ArrowRight' && currentStep < steps.length - 1) setCurrentStep(s => s + 1);
      if (e.key === 'ArrowLeft' && currentStep > 0) setCurrentStep(s => s - 1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentStep, steps.length, onEnd]);

  if (!tour || !step) return null;

  // Calculate tooltip position
  let tooltipStyle = {};
  if (targetRect) {
    const pos = step.position || 'bottom';
    if (pos === 'bottom') {
      tooltipStyle = {
        top: targetRect.top + targetRect.height + 12,
        left: Math.max(16, targetRect.left + targetRect.width / 2 - 160),
      };
    } else if (pos === 'top') {
      tooltipStyle = {
        top: targetRect.top - 12,
        left: Math.max(16, targetRect.left + targetRect.width / 2 - 160),
        transform: 'translateY(-100%)',
      };
    } else if (pos === 'right') {
      tooltipStyle = {
        top: targetRect.top + targetRect.height / 2 - 50,
        left: targetRect.left + targetRect.width + 12,
      };
    } else {
      tooltipStyle = {
        top: targetRect.top + targetRect.height / 2 - 50,
        left: targetRect.left - 12,
        transform: 'translateX(-100%)',
      };
    }
  } else {
    // Center tooltip if target not found
    tooltipStyle = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none" data-testid="guided-tour-overlay">
      {/* Dimmed overlay with cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'auto' }}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="6"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#tour-mask)"
          onClick={onEnd}
        />
      </svg>

      {/* Highlight ring */}
      {targetRect && (
        <div
          className="absolute rounded-md pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 3px rgba(217,70,239,0.6), 0 0 20px rgba(217,70,239,0.25)',
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        ref={tooltipRef}
        className="absolute pointer-events-auto w-[320px]"
        style={{ ...tooltipStyle, transition: 'all 0.3s ease' }}
        data-testid="guided-tour-tooltip"
      >
        <div className="bg-zinc-900 border border-fuchsia-500/40 rounded-lg shadow-xl shadow-black/40 p-4">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-fuchsia-400 tracking-wider uppercase">
              Step {currentStep + 1} of {steps.length}
            </span>
            <button
              onClick={onEnd}
              className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-white transition-colors"
              data-testid="tour-close-btn"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className="w-full h-1 bg-zinc-800 rounded-full mb-3">
            <div
              className="h-full bg-fuchsia-500 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>

          <h4 className="text-sm font-semibold text-zinc-100 mb-1.5">{step.title}</h4>
          <p className="text-xs text-zinc-400 leading-relaxed mb-1">
            {step.content}
          </p>
          {!targetRect && step.fallbackText && (
            <p className="text-[11px] text-amber-400/70 mt-1 italic">{step.fallbackText}</p>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-zinc-800">
            <button
              onClick={onEnd}
              className="text-[11px] text-zinc-500 hover:text-zinc-300 flex items-center gap-1 transition-colors"
              data-testid="tour-skip-btn"
            >
              <SkipForward className="w-3 h-3" /> Skip Tour
            </button>
            <div className="flex items-center gap-2">
              <button
                disabled={currentStep === 0}
                onClick={() => setCurrentStep(s => s - 1)}
                className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={() => setCurrentStep(s => s + 1)}
                  className="px-3 py-1.5 bg-fuchsia-600 hover:bg-fuchsia-500 text-white text-xs rounded-md font-medium transition-colors"
                  data-testid="tour-next-btn"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={onEnd}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs rounded-md font-medium transition-colors"
                  data-testid="tour-finish-btn"
                >
                  Finish
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuidedTour;
